/**
 * Higher-level reverse-engineering of a parsed TouchOSC layout —
 * port of tools/tosc_analyze.py.
 *
 * Takes the flat record list from parse.ts and produces a structured
 * analysis: faders, buttons grouped into visual rows, channel/CC
 * usage tables, group hierarchy, unsupported feature flags. The
 * Settings → TouchOSC section renders directly from this.
 */

import type { ToscRecord, ToscMidiInfo as ToscMidiInfoParse } from './parse.ts';
import type {
  PositionedRecord,
  CountBuckets,
  MidiCcUse,
  MidiNoteUse,
  OscRouteUse,
  ButtonRow,
  UnsupportedFlag,
  ToscAnalysis,
  ToscMidiInfo,
} from '@opbuttons/schema';

// Re-export the shared shapes so existing callers don't need to update imports.
export type {
  PositionedRecord,
  CountBuckets,
  MidiCcUse,
  MidiNoteUse,
  OscRouteUse,
  ButtonRow,
  UnsupportedFlag,
  ToscAnalysis,
};

// Compile-time check that the parser's MIDI shape matches the schema's.
const _midiShapeCheck: ToscMidiInfo extends ToscMidiInfoParse ? true : never = true;
void _midiShapeCheck;

// ───── Helpers ─────────────────────────────────────────────────────

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export function noteName(n: string | number): string {
  const v = typeof n === 'number' ? n : Number.parseInt(String(n), 10);
  if (!Number.isFinite(v)) return `?(${n})`;
  const octave = Math.floor(v / 12) - 1;
  return `${NOTE_NAMES[((v % 12) + 12) % 12]}${octave}`;
}

function toPx(v: string | undefined): number {
  if (!v) return 0;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function fmtColor(c: ToscRecord['color']): string {
  if (!c) return '';
  const r = Math.round(Number.parseFloat(c.r || '0') * 255);
  const g = Math.round(Number.parseFloat(c.g || '0') * 255);
  const b = Math.round(Number.parseFloat(c.b || '0') * 255);
  const a = Number.parseFloat(c.a || '1');
  const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  return a < 0.99 ? `${hex}@${a.toFixed(2)}` : hex;
}

function overlaps(a: PositionedRecord, b: PositionedRecord): boolean {
  return !(
    a.x + a.w <= b.x ||
    b.x + b.w <= a.x ||
    a.y + a.h <= b.y ||
    b.y + b.h <= a.y
  );
}

function centerDistance(a: PositionedRecord, b: PositionedRecord): number {
  const ax = a.x + a.w / 2;
  const ay = a.y + a.h / 2;
  const bx = b.x + b.w / 2;
  const by = b.y + b.h / 2;
  return Math.hypot(ax - bx, ay - by);
}

// ───── Conversion + analysis ───────────────────────────────────────

function positionedFromRecord(r: ToscRecord): PositionedRecord {
  let midi: ToscMidiInfo | undefined;
  const userOsc: { path: string; args: string }[] = [];
  for (const m of r.messages) {
    if (m.kind === 'midi' && midi === undefined) {
      midi = m.info;
    } else if (m.kind === 'osc' && !m.info.boilerplate) {
      userOsc.push({ path: m.info.path, args: m.info.args });
    }
  }
  return {
    id: r.id,
    type: r.type,
    name: r.name,
    text: r.text,
    x: toPx(r.frame?.x),
    y: toPx(r.frame?.y),
    w: toPx(r.frame?.w),
    h: toPx(r.frame?.h),
    color: fmtColor(r.color),
    parents: r.parents,
    label: '', // filled by associateLabels
    midi,
    userOsc,
    hasScript: r.script.length > 0,
  };
}

function associateLabels(records: PositionedRecord[]): void {
  const labels = records.filter(
    (r) => (r.type === 'LABEL' || r.type === 'TEXT') && r.text,
  );
  for (const r of records) {
    if (r.type === 'LABEL' || r.type === 'TEXT' || r.type === 'GROUP' || r.type === 'BOX') {
      continue;
    }
    const overlap = labels.filter((L) => overlaps(r, L));
    if (overlap.length > 0) {
      const best = overlap.reduce((a, b) =>
        centerDistance(r, a) <= centerDistance(r, b) ? a : b,
      );
      r.label = best.text;
      continue;
    }
    const near = labels.filter((L) => centerDistance(r, L) < 40);
    if (near.length > 0) {
      const best = near.reduce((a, b) =>
        centerDistance(r, a) <= centerDistance(r, b) ? a : b,
      );
      r.label = best.text;
    }
  }
}

function rowGroup(records: PositionedRecord[], tolerance = 15): ButtonRow[] {
  const interactive = records
    .filter((r) => r.type === 'BUTTON' || r.type === 'FADER')
    .slice()
    .sort((a, b) => (a.y - b.y) || (a.x - b.x));
  const rows: ButtonRow[] = [];
  let current: PositionedRecord[] = [];
  let currentY: number | null = null;
  for (const r of interactive) {
    if (currentY === null || Math.abs(r.y - currentY) <= tolerance) {
      current.push(r);
      if (currentY === null) currentY = r.y;
    } else {
      current.sort((a, b) => a.x - b.x);
      const ys = current.map((c) => c.y);
      rows.push({ yMin: Math.min(...ys), yMax: Math.max(...ys), buttons: current });
      current = [r];
      currentY = r.y;
    }
  }
  if (current.length > 0) {
    current.sort((a, b) => a.x - b.x);
    const ys = current.map((c) => c.y);
    rows.push({ yMin: Math.min(...ys), yMax: Math.max(...ys), buttons: current });
  }
  // Only return rows that contain at least one BUTTON (matches Python output).
  return rows.filter((row) => row.buttons.some((b) => b.type === 'BUTTON'));
}

function detectUnsupported(records: PositionedRecord[]): UnsupportedFlag[] {
  const flags: UnsupportedFlag[] = [];
  // NOTE: FADER used to flag here ("OPButtons has the FaderItem schema but no
  // component yet") - stale since DEC-064 shipped the real Fader component
  // (2026-07-14-ish). Removed 2026-08-10 after verifying live against the
  // corpus (`scratchpad/tosc-corpus.mts --section flags`): 13 faders in
  // `iPad1 61.tosc` and 6 in `ortoPilot Live Stream40.tosc` were both
  // raising a false "not supported" warning. RADIAL is a comparable case
  // (DEC-116 shipped a `radial` kind) and per TOSC-IMPORT-DESIGN.md's own
  // mapping table is destined to become supported too, not flagged - left
  // for whoever builds importer step (2) onward, since deciding its exact
  // flag/no-flag treatment is part of that build, not a standalone fix.
  const scripted = records.filter((r) => r.hasScript).length;
  if (scripted > 0) {
    flags.push({
      code: 'lua-script',
      message: `${scripted} control${scripted === 1 ? '' : 's'} carry Lua script bodies - OPButtons has no Lua interpreter; behaviour will be lost on import.`,
      count: scripted,
    });
  }
  const noteonButtons = records.filter(
    (r) => r.type === 'BUTTON' && r.midi?.type === 'NOTE_ON',
  ).length;
  if (noteonButtons > 0) {
    flags.push({
      code: 'midi-noteon-on-button',
      message: `${noteonButtons} button${noteonButtons === 1 ? '' : 's'} send NoteOn - OPButtons supports note-on buttons via the advanced action editor.`,
      count: noteonButtons,
    });
  }
  const oscRouted = records.filter((r) => r.userOsc.length > 0).length;
  if (oscRouted > 0) {
    flags.push({
      code: 'osc-routing',
      message: `${oscRouted} control${oscRouted === 1 ? '' : 's'} use OSC routing - OPButtons's importer will preserve the path templates but not the partial-expansion grammar.`,
      count: oscRouted,
    });
  }
  const midiReceive = records.filter((r) => r.midi?.receive).length;
  if (midiReceive > 0) {
    flags.push({
      code: 'midi-receive',
      message: `${midiReceive} control${midiReceive === 1 ? '' : 's'} listen for incoming MIDI (bidirectional sync) - OPButtons's Matrix tracks routing in one direction only.`,
      count: midiReceive,
    });
  }
  return flags;
}

// ───── Public entry point ─────────────────────────────────────────

export function analyze(records: ToscRecord[], fileName: string): ToscAnalysis {
  const positioned = records.map(positionedFromRecord);
  associateLabels(positioned);

  const counts: CountBuckets = {
    byType: {},
    records: records.length,
    midiMessages: 0,
    oscUserMessages: 0,
    oscBoilerplateMessages: 0,
    scriptedNodes: 0,
  };
  for (const r of records) {
    counts.byType[r.type] = (counts.byType[r.type] ?? 0) + 1;
    for (const m of r.messages) {
      if (m.kind === 'midi') counts.midiMessages += 1;
      else if (m.kind === 'osc') {
        if (m.info.boilerplate) counts.oscBoilerplateMessages += 1;
        else counts.oscUserMessages += 1;
      }
    }
    if (r.script) counts.scriptedNodes += 1;
  }

  const faders = positioned.filter((r) => r.type === 'FADER');
  const buttonRows = rowGroup(positioned);

  // ── OSC user routes ──
  const oscRouteMap = new Map<string, OscRouteUse>();
  for (const r of positioned) {
    for (const o of r.userOsc) {
      const existing = oscRouteMap.get(o.path);
      const use = { type: r.type, name: r.name, label: r.label };
      if (existing) existing.uses.push(use);
      else oscRouteMap.set(o.path, { path: o.path, uses: [use] });
    }
  }
  const oscRoutes = [...oscRouteMap.values()];

  // ── MIDI usage tables ──
  const ccMap = new Map<string, MidiCcUse>();
  const noteMap = new Map<string, MidiNoteUse>();
  for (const r of positioned) {
    const m = r.midi;
    if (!m) continue;
    if (m.type === 'CONTROLCHANGE') {
      const key = `${m.channel}|${m.data1}`;
      const use = { name: r.name, label: r.label, defaultValue: m.data2 };
      const existing = ccMap.get(key);
      if (existing) existing.uses.push(use);
      else ccMap.set(key, { channel: m.channel, cc: m.data1, uses: [use] });
    } else if (m.type === 'NOTE_ON') {
      const key = `${m.channel}|${m.data1}`;
      const use = { name: r.name, label: r.label, defaultValue: m.data2 };
      const existing = noteMap.get(key);
      if (existing) existing.uses.push(use);
      else noteMap.set(key, {
        channel: m.channel,
        note: m.data1,
        noteName: noteName(m.data1),
        uses: [use],
      });
    }
  }
  const midiCcUses = [...ccMap.values()].sort((a, b) =>
    (Number(a.channel) - Number(b.channel)) || (Number(a.cc) - Number(b.cc)),
  );
  const midiNoteUses = [...noteMap.values()].sort((a, b) =>
    (Number(a.channel) - Number(b.channel)) || (Number(a.note) - Number(b.note)),
  );

  const groups = records
    .filter((r) => r.type === 'GROUP')
    .map((r) => ({
      name: r.name,
      parents: r.parents,
      frame: {
        x: toPx(r.frame?.x),
        y: toPx(r.frame?.y),
        w: toPx(r.frame?.w),
        h: toPx(r.frame?.h),
      },
    }));

  const unsupported = detectUnsupported(positioned);

  return {
    fileName,
    counts,
    faders,
    buttonRows,
    oscRoutes,
    midiCcUses,
    midiNoteUses,
    unsupported,
    groups,
  };
}
